import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, Form, Link, ModalContext, SidebarModal, SidebarModalListItem, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import useCreateOrEditAgencyProviderDeadlineGroupModal from './hooks/useCreateOrEditAgencyProviderDeadlineGroupModal'

const confirmDelete = (props, deleteFn) => {
  const {
    callbacks: {
      closeModal,
      deleteAgencyProviderDeadlineGroup,
    },
  } = props

  const deleteAgencyProviderDeadlineGroupPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Deadline Group Deleted',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, remove it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Remove Deadline Group?',
    })
    .then(({ value }) => {
      if (value){
        deleteAgencyProviderDeadlineGroup(deleteAgencyProviderDeadlineGroupPayload)
      }
    })
}

const createOrEditAgencyProviderDeadlineGroup = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: {
      closeModal, createAgencyProviderDeadlineGroup, updateAgencyProviderDeadlineGroup,
    },
  } = props

  const text = entityState.id ? 'Updated' : 'Added'
  const actionFn = entityState.id ? updateFn : createFn

  const agencyProviderDeadlineGroupPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Deadline Group ${text}`,
  }

  if (entityState.id) return updateAgencyProviderDeadlineGroup(agencyProviderDeadlineGroupPayload)

  return createAgencyProviderDeadlineGroup(agencyProviderDeadlineGroupPayload)
}

const CreateOrEditAgencyProviderDeadlineGroupModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditAgencyProviderDeadlineGroupModal', showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { agencyProviderDeadlineGroup } = modalPayload

  const {
    callbacks: {
      createAgencyProviderDeadlineGroup: createFn,
      createOrEditAgencyProvider,
      deleteAgencyProviderDeadlineGroup: deleteFn,
      handleChangeProvider,
      updateAgencyProviderDeadlineGroup: updateFn,
    },
    creating,
    deadlines,
    deleting,
    entityState,
    errors,
    filteredProviderDeadlineGroups,
    handlers,
    hasDeadlines,
    providersList,
    saveEnabled,
    services,
    updating,
  } = useCreateOrEditAgencyProviderDeadlineGroupModal(agencyProviderDeadlineGroup)

  const {
    id,
    provider_deadline_group_id,
    provider_id,
  } = entityState

  const saveButtonText = id ? 'Update Deadline Group' : 'Create Deadline Group'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Assign'} titleSecondLine="Deadline Group" />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>Can&apos;t find the provider you need?</SidebarNoticeBox.Title>
            <SidebarNoticeBox.Paragraph>
              Assign a new provider <Link onClick={() => createOrEditAgencyProvider()}>here</Link>.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>

          <Form.Field boxProps={{ marginBottom: 'large' }} errorMessage={errors.provider_id} label="* Provider">
            <select
              onChange={e => handleChangeProvider(e.target.value)}
              value={provider_id}
            >
              <option value="">Please Select...</option>
              {providersList.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.title}
                </option>
              ))}
            </select>
          </Form.Field>

          <Form.Field errorMessage={errors.provider_deadline_group_id} label="* Deadline Group">
            <select
              name="provider_deadline_group_id"
              value={provider_deadline_group_id}
              {...handlers}
            >
              <option value="">Please Select...</option>
              {filteredProviderDeadlineGroups.map(providerDeadlineGroup => (
                <option key={providerDeadlineGroup.id} value={providerDeadlineGroup.id}>
                  {providerDeadlineGroup.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {hasDeadlines && (
            <Form.Field label="Deadlines" marginTop="large">
              {Object.entries(deadlines).map((deadline) => {
                const [key, value] = deadline
                const serviceId = key.split('_')[1]
                const service = services[serviceId] || {}

                if (!value.time) return null

                return (
                  <SidebarModalListItem
                    key={serviceId}
                    title={service.title}
                  >
                    <SidebarModalListItem.TertiaryText>
                      {value.time}
                    </SidebarModalListItem.TertiaryText>
                  </SidebarModalListItem>
                )
              })}
            </Form.Field>
          )}

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(props, deleteFn)}
                size="medium"
              >
                Remove Deadline Group
              </Button>
            </Form.Field>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrEditAgencyProviderDeadlineGroup(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditAgencyProviderDeadlineGroupModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyProviderDeadlineGroupModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
